import type { ICampaignGroup } from '~/modules/campaign-group';

import { isCampaignInGroupDifferent } from '~/modules/campaign-template';
import { useCampaignGroupStore } from '~/modules/campaign-group';
import { useTransientState } from '~/hooks/useTransientState';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { isCampaignGroupDifferent } from '../utils';
import { useMemo } from 'react';

export const useCGTemplateCheckState = (campaignGroup: ICampaignGroup | undefined) => {
  const storeCampaigns = useCampaignGroupStore((state) => state.campaignGroups);
  const campaignGroupCheck = useMemo(() => {
    if (!campaignGroup?.id || !storeCampaigns) return;

    return storeCampaigns.find((c) => c.id === campaignGroup.id);
  }, [campaignGroup?.id, storeCampaigns]);

  const isShowDiscardRaw = useMemo(() => {
    if (!campaignGroup?.id || !campaignGroup || !campaignGroupCheck) return false;

    const activeCampaigns = campaignGroup.campaigns?.filter((c) => !c.deletedAt) ?? [];
    const activeCampaignsCheck = campaignGroupCheck.campaigns?.filter((c) => !c.deletedAt) ?? [];

    const activeCampaignGroup = { ...campaignGroup, campaigns: activeCampaigns } as ICampaignGroup;
    const activeCampaignGroupCheck = {
      ...campaignGroupCheck,
      campaigns: activeCampaignsCheck,
    } as ICampaignGroup;

    const isHasCGChange = isCampaignGroupDifferent(activeCampaignGroup, activeCampaignGroupCheck);
    if (isHasCGChange) return true;

    // Check campaigns array
    if (activeCampaigns.length !== activeCampaignsCheck.length) return true;

    return activeCampaigns.some((campaign, index) =>
      isCampaignInGroupDifferent(campaign, activeCampaignsCheck[index]),
    );
  }, [campaignGroup, campaignGroupCheck]);

  const { state: isShowDiscard } = useTransientState(isShowDiscardRaw);

  const isDisabledEditing = useMemo(() => {
    if (!campaignGroup?.status) return true;

    return [CAMPAIGN_STATUS.STOPPED, CAMPAIGN_STATUS.COMPLETED].includes(campaignGroup.status);
  }, [campaignGroup?.status]);

  const isDisableAddTemplates = useMemo(() => {
    if (isDisabledEditing || campaignGroup?.status === CAMPAIGN_STATUS.RUNNING) return true;

    return false;
  }, [campaignGroup?.status, isDisabledEditing]);

  return { isShowDiscard, isDisabledEditing, isDisableAddTemplates };
};
